import React, { useState, useMemo } from 'react';
import { ChevronLeft } from 'lucide-react';
import Tooltip from './Tooltip';
import {
  Chart as ChartJS,
  Tooltip as ChartTooltip, Legend,
  ArcElement,
  CategoryScale, LinearScale, BarElement,
  LineElement, PointElement, Filler,
} from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import { useUserData } from '../context/UserDataContext';

ChartJS.register(
  ArcElement, ChartTooltip, Legend,
  CategoryScale, LinearScale, BarElement,
  LineElement, PointElement, Filler,
);

// ── Utilities ─────────────────────────────────────────────────────────────────

const fmt = (n) => '$' + Math.round(Math.max(0, n)).toLocaleString();
const fmtSigned = (n) => (n >= 0 ? '+$' : '-$') + Math.abs(Math.round(n)).toLocaleString();
const fmtPct = (n, dp = 2) => (isFinite(n) ? n.toFixed(dp) + '%' : '—');

/** FV with contributions, honouring compounding frequency for the stated rate */
function calcFV(pv, monthlyPmt, annualRatePct, years, freqPerYear = 12) {
  if (years <= 0) return pv;
  const r = annualRatePct / 100;
  const ear = Math.pow(1 + r / freqPerYear, freqPerYear) - 1; // effective annual rate
  const emr = Math.pow(1 + ear, 1 / 12) - 1;                 // effective monthly rate
  const n = Math.round(years * 12);
  if (emr < 1e-10) return pv + monthlyPmt * n;
  return pv * Math.pow(1 + emr, n) + monthlyPmt * (Math.pow(1 + emr, n) - 1) / emr;
}

/** PV: how much to invest today to reach fv in n years */
function calcPV(fv, annualRatePct, years, freqPerYear = 12) {
  if (years <= 0) return fv;
  const r = annualRatePct / 100;
  const ear = Math.pow(1 + r / freqPerYear, freqPerYear) - 1;
  if (ear < 1e-10) return fv;
  return fv / Math.pow(1 + ear, years);
}

/** Binary-search for months needed to reach goal */
function monthsToGoal(goal, pv, pmt, annualRatePct) {
  if (!goal || goal <= 0) return 0;
  if (pv >= goal) return 0;
  const r = annualRatePct / 100 / 12;
  if (r <= 0) return pmt > 0 ? Math.max(0, (goal - pv) / pmt) : Infinity;
  let lo = 0, hi = 1500;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    const fv = pv * Math.pow(1 + r, mid) + pmt * (Math.pow(1 + r, mid) - 1) / r;
    if (fv < goal) lo = mid; else hi = mid;
    if (hi - lo < 0.05) break;
  }
  return hi > 1490 ? Infinity : hi;
}

/** Binary-search annual return % needed to reach goal */
function solveForRate(goal, pv, pmt, years) {
  if (!goal || goal <= 0 || years <= 0) return 0;
  const n = years * 12;
  if (pv + pmt * n >= goal) return 0; // 0% or less needed
  let lo = 0, hi = 2.0; // 0–200%
  for (let i = 0; i < 150; i++) {
    const r = (lo + hi) / 2;
    const mr = r / 12;
    const fv = pv * Math.pow(1 + mr, n) + pmt * (Math.pow(1 + mr, n) - 1) / mr;
    if (fv < goal) lo = r; else hi = r;
    if (hi - lo < 1e-5) break;
  }
  return ((lo + hi) / 2) * 100;
}

/** Lump sum needed today so that pmt + lump sum together reach goal */
function solveLumpSum(goal, pmt, annualRatePct, years) {
  const r = annualRatePct / 100 / 12;
  const n = years * 12;
  if (r < 1e-10) return Math.max(0, goal - pmt * n);
  const fvPmt = pmt * (Math.pow(1 + r, n) - 1) / r;
  const pv = (goal - fvPmt) / Math.pow(1 + r, n);
  return Math.max(0, pv);
}

/** Monthly payment needed to reach goal from starting balance */
function solvePMT(goal, pv, annualRatePct, years) {
  const r = annualRatePct / 100 / 12;
  const n = years * 12;
  if (n <= 0) return 0;
  if (r < 1e-10) return Math.max(0, (goal - pv) / n);
  const growth = Math.pow(1 + r, n);
  const pmt = (goal - pv * growth) * r / (growth - 1);
  return Math.max(0, pmt);
}

/** Estimate federal marginal tax rate from income + filing status */
function estimateMarginalRate(annualIncome, filingStatus) {
  const brackets = filingStatus === 'married_filing_jointly'
    ? [[23200, 10], [94300, 12], [201050, 22], [383900, 24], [487450, 32], [731200, 35], [Infinity, 37]]
    : [[11600, 10], [47150, 12], [100525, 22], [191950, 24], [243725, 32], [609350, 35], [Infinity, 37]];
  for (const [limit, rate] of brackets) {
    if (annualIncome <= limit) return rate;
  }
  return 37;
}

function fmtMonths(m) {
  if (!isFinite(m)) return 'Never at this rate';
  const yr = Math.floor(m / 12);
  const mo = Math.round(m % 12);
  if (yr === 0) return `${mo} month${mo !== 1 ? 's' : ''}`;
  if (mo === 0) return `${yr} year${yr !== 1 ? 's' : ''}`;
  return `${yr} yr ${mo} mo`;
}

// ── Shared UI ─────────────────────────────────────────────────────────────────

function BackLink({ onClick, label = 'Back to Calculators' }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-6 transition-colors"
    >
      <ChevronLeft size={15} /> {label}
    </button>
  );
}

function CalcCard({ children, className = '' }) {
  return (
    <div className={`bg-slate-800 border border-slate-700 rounded-xl p-5 ${className}`}>
      {children}
    </div>
  );
}

function InputRow({ label, prefix, suffix, value, onChange, type = 'number', min = 0, max, step, helper, tooltip }) {
  return (
    <div>
      <div className="flex items-center mb-1.5">
        <label className="text-xs text-slate-500 uppercase tracking-wider">{label}</label>
        {tooltip && <Tooltip text={tooltip} />}
      </div>
      <div className="flex items-center bg-slate-700/60 border border-slate-600 rounded-xl px-3 py-2.5 gap-2 focus-within:border-blue-500 transition-colors">
        {prefix && <span className="text-slate-400 font-medium flex-shrink-0">{prefix}</span>}
        <input
          type={type}
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={e => onChange(e.target.value)}
          className="flex-1 bg-transparent text-slate-100 font-semibold focus:outline-none tabular-nums min-w-0"
        />
        {suffix && <span className="text-slate-400 text-sm flex-shrink-0">{suffix}</span>}
      </div>
      {helper && <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">{helper}</p>}
    </div>
  );
}

function SliderRow({ label, value, onChange, min, max, step, display, color = 'blue', tooltip }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center">
          <label className="text-xs text-slate-500 uppercase tracking-wider">{label}</label>
          {tooltip && <Tooltip text={tooltip} />}
        </div>
        <span className={`text-sm font-bold text-${color}-400`}>{display}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className={`w-full accent-${color}-500 h-1.5 rounded-full`}
      />
    </div>
  );
}

function BigStat({ label, value, color = 'text-emerald-400', sub }) {
  return (
    <div>
      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-3xl font-bold tabular-nums ${color}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, color = 'text-slate-200' }) {
  return (
    <div className="bg-slate-700/40 rounded-lg p-3">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className={`text-base font-bold tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

function CalcDesc({ children }) {
  return <p className="text-sm text-slate-400 leading-relaxed mb-6">{children}</p>;
}

// ── Hub ───────────────────────────────────────────────────────────────────────

const CALC_CARDS = [
  { id: 'portfolio-yield',        emoji: '📈', title: 'Portfolio Yield',          desc: 'Measure your real return before and after taxes' },
  { id: 'savings-goal',           emoji: '🎯', title: 'Savings Goal',             desc: 'Find out when you\'ll hit your target' },
  { id: 'pv-fv',                  emoji: '⏳', title: 'Present & Future Value',   desc: 'See what your money is worth across time' },
  { id: 'goal-solver',            emoji: '🔧', title: 'Goal Solver',              desc: 'Solve for rate, lump sum, or monthly savings' },
  { id: 'retirement-comparison',  emoji: '🏦', title: '401k vs Roth vs Brokerage', desc: 'See which account wins at your tax rate' },
];

export function CalculatorHub({ onNavigate, onBack }) {
  return (
    <div>
      <BackLink onClick={onBack} label="All Topics" />
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-100">Financial Calculators</h1>
        <p className="text-slate-400 text-sm mt-1">Interactive tools to plan your financial future.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {CALC_CARDS.map(c => (
          <button
            key={c.id}
            onClick={() => onNavigate(c.id)}
            className="text-left bg-slate-800/50 border border-slate-700/60 rounded-2xl p-5 hover:border-slate-500 hover:bg-slate-800 transition-all group"
          >
            <div className="text-3xl mb-3">{c.emoji}</div>
            <h3 className="text-base font-bold text-slate-200 group-hover:text-white mb-1 transition-colors">
              {c.title}
            </h3>
            <p className="text-sm text-slate-400 mb-4 leading-snug">{c.desc}</p>
            <div className="text-xs text-blue-400 group-hover:text-blue-300 font-medium transition-colors">
              Open calculator →
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Calculator 1: Portfolio Yield ─────────────────────────────────────────────

export function PortfolioYield({ onBack }) {
  const { userProfile, budgetData } = useUserData();

  const today      = new Date().toISOString().slice(0, 10);
  const threeYrAgo = new Date(Date.now() - 3 * 365.25 * 86400000).toISOString().slice(0, 10);
  const oneYrAgo   = new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10);

  const annualIncome = (userProfile?.annualIncome || 0) || (budgetData?.income || 0) * 12;
  const profileTaxRate = annualIncome > 0
    ? estimateMarginalRate(annualIncome, userProfile?.taxFilingStatus || 'single')
    : 24;

  const [startDate,     setStartDate]     = useState(threeYrAgo);
  const [endDate,       setEndDate]       = useState(today);
  const [startBal,      setStartBal]      = useState('50000');
  const [currentBal,    setCurrentBal]    = useState('72000');
  const [contributions, setContributions] = useState('10000');
  const [feesTotal,     setFeesTotal]     = useState('300');
  const [taxRate,       setTaxRate]       = useState(String(profileTaxRate));

  const applyExample = (ex) => {
    if (ex === 'good') {
      setStartDate(threeYrAgo); setEndDate(today);
      setStartBal('50000'); setCurrentBal('72000');
      setContributions('10000'); setFeesTotal('300'); setTaxRate('24');
    } else {
      setStartDate(oneYrAgo); setEndDate(today);
      setStartBal('50000'); setCurrentBal('52000');
      setContributions('0'); setFeesTotal('500'); setTaxRate('24');
    }
  };

  const results = useMemo(() => {
    const B0 = parseFloat(startBal)      || 0;
    const B1 = parseFloat(currentBal)    || 0;
    const C  = parseFloat(contributions) || 0;
    const F  = parseFloat(feesTotal)     || 0;
    const T  = parseFloat(taxRate)       || 0;

    if (B0 <= 0 && B1 <= 0) return null;

    const totalInvested = B0 + C;
    const grossGain     = B1 - totalInvested + F;
    const netGain       = B1 - totalInvested;
    const taxOnGains    = netGain > 0 ? netGain * (T / 100) : 0;
    const afterTaxGain  = netGain - taxOnGains;

    const grossReturn    = totalInvested > 0 ? (grossGain    / totalInvested) * 100 : 0;
    const afterTaxReturn = totalInvested > 0 ? (afterTaxGain / totalInvested) * 100 : 0;

    const d0 = new Date(startDate);
    const d1 = new Date(endDate);
    const years = Math.max((d1 - d0) / (365.25 * 86400000), 0.01);
    const annualizedReturn = totalInvested > 0 && B1 > 0
      ? (Math.pow(B1 / totalInvested, 1 / years) - 1) * 100
      : 0;

    const feeTaxDrag = F + taxOnGains;
    const donutData  = [Math.max(0, B0), Math.max(0, C), Math.max(0, grossGain)];

    return { grossReturn, afterTaxReturn, annualizedReturn, grossGain, netGain, afterTaxGain, feeTaxDrag, taxOnGains, donutData, totalInvested, B1, F, T };
  }, [startDate, endDate, startBal, currentBal, contributions, feesTotal, taxRate]);

  const chartData = {
    labels: ['Starting Balance', 'Contributions', 'Investment Gain'],
    datasets: [{
      data: results?.donutData || [1, 0, 0],
      backgroundColor: ['#475569', '#3b82f6', '#10b981'],
      borderColor: ['#334155', '#2563eb', '#059669'],
      borderWidth: 2,
    }],
  };

  const chartOpts = {
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { color: '#94a3b8', font: { size: 11 }, padding: 12 } },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${fmt(ctx.raw)}` } },
    },
    cutout: '65%',
    animation: { animateRotate: true, duration: 600 },
  };

  // Tooltip content
  const TT = {
    startBal:      'The exact value of this account on your chosen start date. Log into your brokerage and pull up the statement from that month.',
    currentBal:    'What your account is worth right now at today\'s prices. Log in and check.',
    contributions: 'This is the grand total of ALL new money you put in over the entire period. Not per month or per year — the total ever. If you added $200/month for 2 years, enter $4,800.',
    fees:          'Every dollar charged by your broker over this period, added up. Look for "fees", "commissions", or "expense ratio" in your statements. Enter $0 if you\'re on a zero-fee platform like Robinhood or Fidelity.',
    taxRate:       'Your income tax bracket — not your effective rate. Most employed people in the US are in the 22% or 24% bracket. Check your last tax return or search "IRS tax brackets 2025".',
  };

  return (
    <div>
      <BackLink onClick={onBack} />
      <h1 className="text-xl font-bold text-slate-100 mb-1">📈 Portfolio Yield</h1>
      <CalcDesc>
        Calculate your real investment return before and after taxes and fees.
        Great for evaluating any account — brokerage, 401(k), or IRA — over any time period.
      </CalcDesc>

      {/* Two-column layout: left = tip+chips+inputs, right = results (sticky) */}
      <div className="grid grid-cols-1 md:grid-cols-[55%_43%] gap-6 items-start">
        {/* LEFT column */}
        <div className="space-y-4">
          {/* How to read this */}
          <div className="bg-blue-950/50 border border-blue-700/40 rounded-xl p-4">
            <div className="text-xs font-bold text-blue-300 uppercase tracking-wider mb-1.5">💡 How to read this</div>
            <p className="text-sm text-slate-300 leading-relaxed">
              This calculator shows how well your investment actually performed. Enter your account's starting
              and ending balances, any money you added along the way, and any fees or taxes paid. We'll calculate
              your real return — before and after the costs that eat into your gains.
            </p>
          </div>

          {/* Try an example chips */}
          <div className="flex gap-2">
            <button
              onClick={() => applyExample('good')}
              className="px-3 py-1.5 bg-emerald-900/30 border border-emerald-700/40 rounded-lg text-xs font-medium text-emerald-300 hover:bg-emerald-900/50 transition-colors"
            >
              📈 Good year
            </button>
            <button
              onClick={() => applyExample('flat')}
              className="px-3 py-1.5 bg-rose-900/30 border border-rose-700/40 rounded-lg text-xs font-medium text-rose-300 hover:bg-rose-900/50 transition-colors"
            >
              📉 Flat year
            </button>
          </div>

          {/* Inputs */}
          <CalcCard>
            <h3 className="text-sm font-semibold text-slate-300 mb-4">Your Numbers</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <InputRow
                  label="Start Date" type="date" value={startDate} onChange={setStartDate}
                  helper="The date you first made this investment or opened this account"
                />
                <InputRow
                  label="End Date" type="date" value={endDate} onChange={setEndDate} max={today}
                  helper="Today's date, or whenever you want to measure up to"
                />
              </div>
              <InputRow
                label="Starting Balance" prefix="$" value={startBal} onChange={setStartBal} step="100"
                tooltip={TT.startBal}
                helper="How much was in this account on your start date. Check your first statement from that month."
              />
              <InputRow
                label="Current Balance" prefix="$" value={currentBal} onChange={setCurrentBal} step="100"
                tooltip={TT.currentBal}
                helper="What the account shows right now. Log into your brokerage to find this."
              />
              <InputRow
                label="Total Contributions" prefix="$" value={contributions} onChange={setContributions} step="100"
                tooltip={TT.contributions}
                helper="The TOTAL extra money you deposited over the ENTIRE time period — not per week or month or year, the grand total of all deposits combined. Example: $500/month for 12 months = enter $6,000. Never added more? Enter $0."
              />
              <InputRow
                label="Total Fees Paid" prefix="$" value={feesTotal} onChange={setFeesTotal} step="10"
                tooltip={TT.fees}
                helper="The TOTAL fees charged over the whole period — not per year. Includes account fees, trading commissions, fund expense ratios. Check brokerage statements under 'fees'. Enter $0 if unsure."
              />
              <InputRow
                label="Marginal Tax Rate" suffix="%" value={taxRate} onChange={setTaxRate} step="1" max="60"
                tooltip={TT.taxRate}
                helper="Your federal income tax bracket %. Most people are at 22% or 24%. Find it on last year's tax return. Used to estimate taxes owed on your gains."
              />
            </div>
          </CalcCard>
        </div>

        {/* RIGHT column — sticky */}
        <div className="space-y-4 md:sticky md:top-6 md:self-start">
          <CalcCard>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <BigStat
                label="Gross return (before tax & fees)"
                value={results ? fmtPct(results.grossReturn) : '—'}
                color="text-emerald-400"
              />
              <BigStat
                label="Your real return (what you kept)"
                value={results ? fmtPct(results.afterTaxReturn) : '—'}
                color="text-blue-400"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <MiniStat label="Average per year"     value={results ? fmtPct(results.annualizedReturn) : '—'} />
              <MiniStat label="Total profit ($)"     value={results ? fmt(results.grossGain) : '—'} color="text-emerald-400" />
              <MiniStat label="Lost to fees & taxes" value={results ? fmt(results.feeTaxDrag) : '—'} color="text-rose-400" />
            </div>
          </CalcCard>

          {/* Donut chart */}
          <CalcCard>
            <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-3">Portfolio breakdown</h3>
            <div className="relative">
              <div style={{ height: 200 }}>
                <Doughnut data={chartData} options={chartOpts} />
              </div>
              {results && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center mt-[-24px]">
                    <div className="text-xs text-slate-500">Total</div>
                    <div className="text-base font-bold text-slate-100">{fmt(results.B1)}</div>
                  </div>
                </div>
              )}
            </div>
          </CalcCard>
        </div>
      </div>

      {/* Breakdown table — full width below both columns */}
      {results && (
        <CalcCard className="mt-6">
          <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-3">Breakdown</h3>
          <div className="space-y-2 text-sm">
            {[
              { label: 'Starting balance',       value: fmt(parseFloat(startBal)||0),          color: 'text-slate-300' },
              { label: 'Contributions added',    value: fmt(parseFloat(contributions)||0),     color: 'text-blue-400' },
              { label: 'Gross investment gain',  value: fmt(results.grossGain),                color: 'text-emerald-400' },
              { label: 'Fees paid',              value: `-${fmt(parseFloat(feesTotal)||0)}`,   color: 'text-rose-400' },
              { label: 'Net gain (after fees)',  value: fmtSigned(results.netGain),            color: results.netGain >= 0 ? 'text-emerald-400' : 'text-rose-400' },
              { label: `Tax on gains (${parseFloat(taxRate)||0}%)`, value: `-${fmt(results.taxOnGains)}`, color: 'text-rose-400' },
              { label: 'After-tax gain',         value: fmtSigned(results.afterTaxGain),       color: results.afterTaxGain >= 0 ? 'text-emerald-400' : 'text-rose-400' },
            ].map(r => (
              <div key={r.label} className="flex justify-between items-center py-1.5 border-b border-slate-700/40 last:border-0">
                <span className="text-slate-400">{r.label}</span>
                <span className={`font-semibold tabular-nums ${r.color}`}>{r.value}</span>
              </div>
            ))}
          </div>
        </CalcCard>
      )}
    </div>
  );
}

// ── Calculator 2: Savings Goal ────────────────────────────────────────────────

function TimelineMilestone({ label, yearLabel, pct }) {
  return (
    <div className="absolute -translate-x-1/2 flex flex-col items-center" style={{ left: `${pct * 100}%` }}>
      <div className="text-xs text-slate-400 whitespace-nowrap mb-1 font-medium">{yearLabel}</div>
      <div className="w-0.5 h-3 bg-slate-500" />
      <div className="w-2 h-2 rounded-full bg-blue-400 mt-0" />
      <div className="text-[10px] text-slate-500 mt-1 whitespace-nowrap">{label}</div>
    </div>
  );
}

export function SavingsGoalCalc({ onBack }) {
  const { userProfile, budgetData } = useUserData();

  const annualIncome = (userProfile?.annualIncome || 0) || (budgetData?.income || 0) * 12;
  const defaultTaxRate = annualIncome > 0
    ? estimateMarginalRate(annualIncome, userProfile?.taxFilingStatus || 'single')
    : 22;

  const [goal,         setGoal]         = useState('100000');
  const [startBal,     setStartBal]     = useState('');
  const [monthlyContrib,setMonthlyContrib]=useState('500');
  const [annualReturn, setAnnualReturn] = useState(7);
  const [taxRate,      setTaxRate]      = useState(defaultTaxRate);

  const results = useMemo(() => {
    const G  = parseFloat(goal) || 0;
    const PV = parseFloat(startBal) || 0;
    const PM = parseFloat(monthlyContrib) || 0;
    const R  = annualReturn;
    const T  = parseFloat(taxRate) || 0;

    if (G <= 0) return null;

    const totalMonths = monthsToGoal(G, PV, PM, R);
    const totalYears  = totalMonths / 12;

    const totalContrib = PM * totalMonths + PV;
    const fv = calcFV(PV, PM, R, totalYears);
    const growth = fv - totalContrib;
    const afterTax = fv - growth * (T / 100);

    // Milestone months
    const m25  = monthsToGoal(G * 0.25, PV, PM, R);
    const m50  = monthsToGoal(G * 0.50, PV, PM, R);
    const m75  = monthsToGoal(G * 0.75, PV, PM, R);

    const milestones = [
      { label: '25%', months: m25,  pct: 0.25 },
      { label: '50%', months: m50,  pct: 0.50 },
      { label: '75%', months: m75,  pct: 0.75 },
      { label: '100%',months: totalMonths, pct: 1.00 },
    ];

    // Chart: year-by-year
    const years = Math.min(Math.ceil(totalYears) + 1, 101);
    const labels = Array.from({ length: years }, (_, i) => `Yr ${i}`);
    const invested = Array.from({ length: years }, (_, i) => PV + PM * 12 * i);
    const portfolio = Array.from({ length: years }, (_, i) => calcFV(PV, PM, R, i));

    return { totalMonths, totalYears, totalContrib, growth, afterTax, milestones, labels, invested, portfolio };
  }, [goal, startBal, monthlyContrib, annualReturn, taxRate]);

  const barData = results ? {
    labels: ['What you put in', 'What you end with'],
    datasets: [
      {
        label: 'Principal',
        data: [results.totalContrib, results.totalContrib],
        backgroundColor: '#3b82f6',
        borderRadius: 4,
      },
      {
        label: 'Returns',
        data: [0, Math.max(0, results.growth)],
        backgroundColor: '#10b981',
        borderRadius: 4,
      },
    ],
  } : null;

  const barOpts = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { labels: { color: '#94a3b8', font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${fmt(ctx.raw)}` } },
    },
    scales: {
      x: { stacked: true, ticks: { color: '#64748b' }, grid: { display: false } },
      y: { stacked: true, ticks: { color: '#64748b', callback: (v) => fmt(v) }, grid: { color: '#1e293b' } },
    },
  };

  return (
    <div>
      <BackLink onClick={onBack} />
      <h1 className="text-xl font-bold text-slate-100 mb-1">🎯 Savings Goal</h1>
      <CalcDesc>
        Enter your savings target and monthly contributions to see exactly when you'll reach it.
        Best for planning a home down payment, college fund, or emergency fund.
      </CalcDesc>

      {/* Goal input — prominent, full width */}
      <CalcCard className="mb-5">
        <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Savings Goal</label>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-slate-400 text-xl font-bold">$</span>
          <input
            type="number" min="0" step="1000" value={goal}
            onChange={e => setGoal(e.target.value)}
            className="flex-1 bg-transparent text-slate-100 text-3xl font-bold focus:outline-none tabular-nums"
            placeholder="100,000"
          />
        </div>
      </CalcCard>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 md:grid-cols-[55%_43%] gap-6 items-start mb-5">
        {/* LEFT: inputs */}
        <CalcCard>
          <div className="space-y-4">
            <InputRow label="Starting Balance ($)"     prefix="$" value={startBal}       onChange={setStartBal}       step="100" />
            <InputRow label="Monthly Contribution ($)" prefix="$" value={monthlyContrib}  onChange={setMonthlyContrib}  step="50" />
            <SliderRow label="Annual Return" value={annualReturn} onChange={setAnnualReturn}
              min={1} max={15} step={0.5} display={`${annualReturn}%`} color="blue"
              tooltip="The average yearly growth rate of your investments. The S&P 500 has historically returned ~7%/year after inflation. Use 6-8% for a diversified stock portfolio, 2-4% for bonds." />
            <InputRow label="Income Tax Rate" suffix="%" value={taxRate} onChange={setTaxRate} step="1" max="60" />
          </div>
        </CalcCard>

        {/* RIGHT: result (sticky) */}
        <div className="md:sticky md:top-6 md:self-start">
          <CalcCard>
            <BigStat
              label="Time to reach goal"
              value={results && isFinite(results.totalMonths) ? fmtMonths(results.totalMonths) : '—'}
              color="text-purple-400"
              sub={results && isFinite(results.totalMonths) ? `at ${annualReturn}% annual return` : 'Adjust inputs above'}
            />
            <div className="grid grid-cols-3 gap-2 mt-5">
              <MiniStat label="Total contributed"  value={results ? fmt(results.totalContrib) : '—'} color="text-blue-400" />
              <MiniStat label="Growth from returns" value={results ? fmt(results.growth) : '—'}       color="text-emerald-400" />
              <MiniStat label="After-tax value"    value={results ? fmt(results.afterTax) : '—'}      color="text-purple-400" />
            </div>
          </CalcCard>
        </div>
      </div>

      {/* Timeline bar — full width */}
      {results && isFinite(results.totalMonths) && (
        <CalcCard className="mb-5 overflow-hidden">
          <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-8">Goal milestones</h3>
          <div className="relative">
            <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-600 to-purple-500 rounded-full w-full transition-all duration-700" />
            </div>
            <div className="relative h-12 mt-1">
              {results.milestones.filter(m => isFinite(m.months)).map(m => (
                <TimelineMilestone
                  key={m.label}
                  label={m.label}
                  yearLabel={fmtMonths(m.months)}
                  pct={m.pct}
                />
              ))}
            </div>
          </div>
        </CalcCard>
      )}

      {/* Stacked bar chart — full width */}
      {barData && (
        <CalcCard>
          <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-3">Principal vs growth</h3>
          <div style={{ height: 240 }}>
            <Bar data={barData} options={barOpts} />
          </div>
        </CalcCard>
      )}
    </div>
  );
}

// ── Calculator 3: PV / FV ─────────────────────────────────────────────────────

const FREQ_OPTIONS = [
  { label: 'Annually',   value: 1   },
  { label: 'Quarterly',  value: 4   },
  { label: 'Monthly',    value: 12  },
  { label: 'Daily',      value: 365 },
];

export function PVFVCalc({ onBack }) {
  const [subtab, setSubtab] = useState('fv');

  // FV inputs
  const [fvPV,   setFvPV]   = useState('10000');
  const [fvPMT,  setFvPMT]  = useState('500');
  const [fvRate, setFvRate] = useState(7);
  const [fvYears,setFvYears]= useState(20);
  const [fvFreq, setFvFreq] = useState(12);

  // PV inputs
  const [pvFV,   setPvFV]   = useState('500000');
  const [pvRate, setPvRate] = useState(7);
  const [pvYears,setPvYears]= useState(20);
  const [pvFreq, setPvFreq] = useState(12);

  const fvResults = useMemo(() => {
    const pv = parseFloat(fvPV)  || 0;
    const pm = parseFloat(fvPMT) || 0;
    const r  = fvRate;
    const y  = fvYears;
    const fv = calcFV(pv, pm, r, y, fvFreq);
    const totalContrib = pv + pm * 12 * y;
    const growth = fv - totalContrib;
    const yearLabels = Array.from({ length: y + 1 }, (_, i) => i === 0 ? 'Start' : `Yr ${i}`);
    const inputLine  = Array.from({ length: y + 1 }, (_, i) => pv + pm * 12 * i);
    const fvLine     = Array.from({ length: y + 1 }, (_, i) => calcFV(pv, pm, r, i, fvFreq));
    const freqComps  = FREQ_OPTIONS.map(f => ({ label: f.label, value: f.value, fv: calcFV(pv, pm, r, y, f.value) }));
    return { fv, totalContrib, growth, yearLabels, inputLine, fvLine, freqComps };
  }, [fvPV, fvPMT, fvRate, fvYears, fvFreq]);

  const pvResults = useMemo(() => {
    const fv = parseFloat(pvFV) || 0;
    const pv = calcPV(fv, pvRate, pvYears, pvFreq);
    return { pv, fv };
  }, [pvFV, pvRate, pvYears, pvFreq]);

  const lineData = {
    labels: fvResults.yearLabels,
    datasets: [
      {
        label: 'What you put in',
        data: fvResults.inputLine,
        borderColor: '#3b82f6',
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.3,
        order: 2,
        fill: false,
      },
      {
        label: 'What you end with',
        data: fvResults.fvLine,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.08)',
        borderWidth: 2.5,
        pointRadius: 0,
        tension: 0.3,
        order: 1,
        fill: '-1',
      },
    ],
  };

  const lineOpts = {
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: '#94a3b8', font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${fmt(ctx.raw)}` } },
    },
    scales: {
      x: { ticks: { color: '#64748b', maxTicksLimit: 8 }, grid: { color: '#1e293b' } },
      y: { ticks: { color: '#64748b', callback: (v) => fmt(v) }, grid: { color: '#1e293b' } },
    },
    animation: { duration: 400 },
  };

  return (
    <div>
      <BackLink onClick={onBack} />
      <h1 className="text-xl font-bold text-slate-100 mb-1">⏳ Present & Future Value</h1>
      <CalcDesc>
        Calculate how much your money grows over time, or how much you need to invest today
        to reach a future target. Understand the real impact of compounding frequency.
      </CalcDesc>

      {/* Compounding tip — full width */}
      <div className="bg-blue-950/50 border border-blue-700/40 rounded-xl p-4 mb-5">
        <div className="text-xs font-bold text-blue-300 uppercase tracking-wider mb-1.5">💡 What is compounding?</div>
        <p className="text-sm text-slate-300 leading-relaxed">
          Your returns earn returns. $1,000 at 7% earns $70 in year 1 — but year 2 you earn 7% on $1,070.
          The more frequently it compounds (monthly vs annually), the faster it grows.
        </p>
      </div>

      {/* Subtabs — full width */}
      <div className="flex bg-slate-800 border border-slate-700 rounded-xl p-1 mb-5 w-fit">
        {[{ id: 'fv', label: 'Future Value' }, { id: 'pv', label: 'Present Value' }].map(t => (
          <button
            key={t.id}
            onClick={() => setSubtab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              subtab === t.id ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subtab === 'fv' && (
        <div className="space-y-5">
          {/* Two-column */}
          <div className="grid grid-cols-1 md:grid-cols-[55%_43%] gap-6 items-start">
            {/* LEFT: inputs */}
            <CalcCard>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">Inputs</h3>
              <div className="space-y-4">
                <InputRow label="Initial Amount ($)" prefix="$" value={fvPV}  onChange={setFvPV}  step="1000" />
                <InputRow label="Monthly Contribution ($)" prefix="$" value={fvPMT} onChange={setFvPMT} step="50" />
                <SliderRow label="Annual Return" value={fvRate}  onChange={setFvRate}  min={1} max={15} step={0.5} display={`${fvRate}%`}
                  tooltip="The average yearly growth rate of your investments. The S&P 500 has historically returned ~7%/year after inflation. Use 6-8% for a diversified stock portfolio, 2-4% for bonds." />
                <SliderRow label="Time Horizon"  value={fvYears} onChange={setFvYears} min={1} max={40} step={1}   display={`${fvYears} yrs`} color="purple" />
                <div>
                  <div className="flex items-center mb-1.5">
                    <label className="text-xs text-slate-500 uppercase tracking-wider">Compounding Frequency</label>
                    <Tooltip text="How often your returns get added back to your balance to start earning more returns. Monthly is most common. More frequent = slightly higher returns for the same annual rate." />
                  </div>
                  <select
                    value={fvFreq}
                    onChange={e => setFvFreq(parseInt(e.target.value))}
                    className="w-full bg-slate-700/60 border border-slate-600 rounded-xl px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                  >
                    {FREQ_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
              </div>
            </CalcCard>

            {/* RIGHT: results (sticky) */}
            <div className="space-y-4 md:sticky md:top-6 md:self-start">
              <CalcCard>
                <BigStat label="Future Value" value={fmt(fvResults.fv)} color="text-amber-400" />
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <MiniStat label="Total contributed"  value={fmt(fvResults.totalContrib)}             color="text-blue-400" />
                  <MiniStat label="Growth from returns" value={fmt(Math.max(0, fvResults.growth))}     color="text-emerald-400" />
                </div>
              </CalcCard>

              <CalcCard>
                <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-3">Compounding frequency comparison</h3>
                <div className="grid grid-cols-2 gap-2">
                  {fvResults.freqComps.map(f => (
                    <div
                      key={f.value}
                      className={`rounded-lg p-2.5 border transition-all ${
                        f.value === fvFreq ? 'border-blue-500 bg-blue-900/20' : 'border-slate-700 bg-slate-700/20'
                      }`}
                    >
                      <div className="text-xs text-slate-500 mb-1">{f.label}</div>
                      <div className={`text-sm font-bold tabular-nums ${f.value === fvFreq ? 'text-blue-300' : 'text-slate-300'}`}>
                        {fmt(f.fv)}
                      </div>
                    </div>
                  ))}
                </div>
              </CalcCard>
            </div>
          </div>

          {/* Line chart — full width */}
          <CalcCard>
            <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-3">Growth over time</h3>
            <div style={{ height: 260 }}>
              <Line data={lineData} options={lineOpts} />
            </div>
          </CalcCard>
        </div>
      )}

      {subtab === 'pv' && (
        <div className="grid grid-cols-1 md:grid-cols-[55%_43%] gap-6 items-start">
          {/* LEFT: inputs */}
          <CalcCard>
            <h3 className="text-sm font-semibold text-slate-300 mb-4">Inputs</h3>
            <div className="space-y-4">
              <InputRow label="Future Amount Needed ($)" prefix="$" value={pvFV} onChange={setPvFV} step="10000" />
              <InputRow label="Annual Return (%)" suffix="%" value={pvRate} onChange={v => setPvRate(parseFloat(v)||0)} step="0.5" max="20" />
              <SliderRow label="Years Away" value={pvYears} onChange={setPvYears} min={1} max={40} step={1} display={`${pvYears} yrs`} color="purple" />
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider mb-1.5 block">Compounding Frequency</label>
                <select
                  value={pvFreq}
                  onChange={e => setPvFreq(parseInt(e.target.value))}
                  className="w-full bg-slate-700/60 border border-slate-600 rounded-xl px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                >
                  {FREQ_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
            </div>
          </CalcCard>

          {/* RIGHT: result (sticky) */}
          <div className="md:sticky md:top-6 md:self-start">
            <CalcCard>
              <BigStat label="You need to invest today" value={fmt(pvResults.pv)} color="text-rose-400" />
              <div className="mt-6 flex items-center gap-2">
                <div className="flex-1 bg-rose-900/20 border border-rose-700/30 rounded-xl p-3 text-center">
                  <div className="text-xs text-slate-500 mb-1">Today</div>
                  <div className="text-base font-bold text-rose-300 tabular-nums">{fmt(pvResults.pv)}</div>
                </div>
                <div className="text-center px-1 flex-shrink-0">
                  <div className="text-xs text-slate-600 whitespace-nowrap">{pvRate}% × {pvYears}yr</div>
                  <div className="text-slate-500 text-lg">→</div>
                </div>
                <div className="flex-1 bg-emerald-900/20 border border-emerald-700/30 rounded-xl p-3 text-center">
                  <div className="text-xs text-slate-500 mb-1">Future</div>
                  <div className="text-base font-bold text-emerald-300 tabular-nums">{fmt(pvResults.fv)}</div>
                </div>
              </div>
            </CalcCard>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Calculator 4: Goal Solver ─────────────────────────────────────────────────

function RiskGauge({ rate }) {
  const maxRate = 15;
  const clampedRate = Math.max(0, Math.min(rate, maxRate));
  const pct = clampedRate / maxRate;
  // Needle rotates from -90° (left, rate=0) to +90° (right, rate=max)
  const needleAngle = -90 + pct * 180;

  // Gauge arc zones: gauge goes left→top→right (counterclockwise in SVG)
  // polarToXY: pct=0 → left, pct=1 → right, through top
  const p2xy = (pct, r, cx = 100, cy = 95) => {
    const a = Math.PI * (1 - pct);
    return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
  };

  const sectorPath = (p1, p2, rOuter = 72, cx = 100, cy = 95) => {
    const [x1, y1] = p2xy(p1, rOuter, cx, cy);
    const [x2, y2] = p2xy(p2, rOuter, cx, cy);
    return `M ${cx} ${cy} L ${x1} ${y1} A ${rOuter} ${rOuter} 0 0 0 ${x2} ${y2} Z`;
  };

  const zones = [
    { p1: 0,     p2: 0.267, color: '#34d399', label: 'Conservative 0–4%' },
    { p1: 0.267, p2: 0.467, color: '#fbbf24', label: 'Moderate 4–7%' },
    { p1: 0.467, p2: 0.667, color: '#fb923c', label: 'Aggressive 7–10%' },
    { p1: 0.667, p2: 1,     color: '#f87171', label: 'Very Aggressive 10%+' },
  ];

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 200 110" className="w-full max-w-xs">
        {zones.map(z => (
          <path key={z.label} d={sectorPath(z.p1, z.p2)} fill={z.color} opacity="0.75" />
        ))}
        {/* Inner fill */}
        <circle cx="100" cy="95" r="40" fill="#1e293b" />
        {/* Needle */}
        <g style={{ transform: `rotate(${needleAngle}deg)`, transformOrigin: '100px 95px', transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
          <rect x="99" y="28" width="2" height="64" rx="1" fill="#f1f5f9" />
          <circle cx="100" cy="95" r="5" fill="#f1f5f9" />
        </g>
        {/* Rate label */}
        <text x="100" y="90" textAnchor="middle" fill="#f1f5f9" fontSize="13" fontWeight="bold">
          {rate > 0 ? `${rate.toFixed(1)}%` : '?'}
        </text>
      </svg>
      {/* Zone legend */}
      <div className="flex flex-wrap justify-center gap-3 mt-1">
        {zones.map(z => (
          <div key={z.label} className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: z.color }} />
            <span className="text-xs text-slate-500">{z.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CircleRing({ pct, amount }) {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const offset = (1 - Math.min(pct, 1)) * circ;
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative">
        <svg width="140" height="140">
          <circle cx="70" cy="70" r={r} fill="none" stroke="#1e293b" strokeWidth="12" />
          <circle
            cx="70" cy="70" r={r}
            fill="none"
            stroke="#8b5cf6"
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            style={{ transform: 'rotate(-90deg)', transformOrigin: '70px 70px', transition: 'stroke-dashoffset 0.5s' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-lg font-bold text-purple-400 tabular-nums">{fmt(amount)}</div>
          <div className="text-xs text-slate-500">/mo</div>
        </div>
      </div>
      <div className="text-xs text-slate-500 text-center">
        {(pct * 100).toFixed(0)}% of $5,000/mo benchmark
      </div>
    </div>
  );
}

function CoinStack({ lumpSum, totalContrib }) {
  const maxH = 120;
  const total = lumpSum + totalContrib;
  const lumpH  = total > 0 ? Math.round((lumpSum / total) * maxH) : 0;
  const contH  = total > 0 ? Math.round((totalContrib / total) * maxH) : 0;
  return (
    <div className="flex items-end justify-center gap-8">
      <div className="flex flex-col items-center gap-2">
        <div className="text-xs text-slate-500">Lump sum today</div>
        <div
          className="w-16 bg-blue-500/70 border-2 border-blue-400 rounded-t-lg transition-all duration-500"
          style={{ height: Math.max(lumpH, 8) }}
        />
        <div className="text-sm font-bold text-blue-300 tabular-nums">{fmt(lumpSum)}</div>
      </div>
      <div className="flex flex-col items-center gap-2">
        <div className="text-xs text-slate-500">Monthly total</div>
        <div
          className="w-16 bg-emerald-500/70 border-2 border-emerald-400 rounded-t-lg transition-all duration-500"
          style={{ height: Math.max(contH, 8) }}
        />
        <div className="text-sm font-bold text-emerald-300 tabular-nums">{fmt(totalContrib)}</div>
      </div>
    </div>
  );
}

export function GoalSolverCalc({ onBack }) {
  const [subtab, setSubtab] = useState('rate');

  // Rate solver
  const [rGoal,  setRGoal]  = useState('500000');
  const [rPV,    setRPV]    = useState('');
  const [rPMT,   setRPMT]   = useState('1000');
  const [rYears, setRYears] = useState('20');

  // Lump sum solver
  const [lGoal,  setLGoal]  = useState('500000');
  const [lPMT,   setLPMT]   = useState('1000');
  const [lRate,  setLRate]  = useState(7);
  const [lYears, setLYears] = useState('20');

  // PMT solver
  const [pGoal,  setPGoal]  = useState('500000');
  const [pPV,    setPPV]    = useState('');
  const [pRate,  setPRate]  = useState(7);
  const [pYears, setPYears] = useState('20');

  const rateResult = useMemo(() => {
    const G = parseFloat(rGoal) || 0;
    const PV = parseFloat(rPV)  || 0;
    const PM = parseFloat(rPMT) || 0;
    const Y  = parseFloat(rYears) || 0;
    return solveForRate(G, PV, PM, Y);
  }, [rGoal, rPV, rPMT, rYears]);

  const lumpResult = useMemo(() => {
    const G = parseFloat(lGoal) || 0;
    const PM = parseFloat(lPMT) || 0;
    const Y  = parseFloat(lYears) || 0;
    const lump = solveLumpSum(G, PM, lRate, Y);
    const totalContrib = PM * 12 * Y;
    return { lump, totalContrib };
  }, [lGoal, lPMT, lRate, lYears]);

  const pmtResult = useMemo(() => {
    const G  = parseFloat(pGoal)  || 0;
    const PV = parseFloat(pPV)    || 0;
    const Y  = parseFloat(pYears) || 0;
    const pmt = solvePMT(G, PV, pRate, Y);
    return { pmt, pct: pmt / 5000 };
  }, [pGoal, pPV, pRate, pYears]);

  const SUBTABS = [
    { id: 'rate', label: 'What rate do I need?' },
    { id: 'lump', label: 'What lump sum today?' },
    { id: 'pmt',  label: 'What monthly savings?' },
  ];

  return (
    <div>
      <BackLink onClick={onBack} />
      <h1 className="text-xl font-bold text-slate-100 mb-1">🔧 Goal Solver</h1>
      <CalcDesc>
        Tell us your goal and we'll solve for whatever you don't know — required return, upfront investment, or monthly savings.
        Use this to reverse-engineer any financial target.
      </CalcDesc>

      {/* Subtabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {SUBTABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubtab(t.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
              subtab === t.id
                ? 'bg-blue-600 border-blue-500 text-white'
                : 'border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Subtab 1 — What rate do I need? */}
      {subtab === 'rate' && (
        <div className="grid grid-cols-1 md:grid-cols-[55%_43%] gap-6 items-start">
          <CalcCard>
            <div className="space-y-4">
              <InputRow label="Target Amount ($)"       prefix="$"  value={rGoal}  onChange={setRGoal}  step="10000" />
              <InputRow label="Starting Balance ($)"    prefix="$"  value={rPV}    onChange={setRPV}    step="1000" />
              <InputRow label="Monthly Contribution ($)"prefix="$"  value={rPMT}   onChange={setRPMT}   step="100" />
              <InputRow label="Years"                   suffix="yr" value={rYears} onChange={setRYears} step="1" min="1" max="50" />
            </div>
          </CalcCard>
          <div className="md:sticky md:top-6 md:self-start">
            <CalcCard>
              <BigStat label="Required Annual Return" value={rateResult > 0 ? fmtPct(rateResult) : '0.0%'} color="text-amber-400" />
              <div className="mt-5">
                <RiskGauge rate={rateResult} />
              </div>
            </CalcCard>
          </div>
        </div>
      )}

      {/* Subtab 2 — Lump sum */}
      {subtab === 'lump' && (
        <div className="grid grid-cols-1 md:grid-cols-[55%_43%] gap-6 items-start">
          <CalcCard>
            <div className="space-y-4">
              <InputRow label="Target Amount ($)"       prefix="$"  value={lGoal}  onChange={setLGoal}  step="10000" />
              <InputRow label="Monthly Contribution ($)"prefix="$"  value={lPMT}   onChange={setLPMT}   step="100" />
              <SliderRow label="Annual Return" value={lRate} onChange={setLRate} min={1} max={15} step={0.5} display={`${lRate}%`}
                tooltip="The average yearly growth rate of your investments. The S&P 500 has historically returned ~7%/year after inflation. Use 6-8% for a diversified stock portfolio, 2-4% for bonds." />
              <InputRow label="Years" suffix="yr" value={lYears} onChange={setLYears} step="1" min="1" max="50" />
            </div>
          </CalcCard>
          <div className="md:sticky md:top-6 md:self-start">
            <CalcCard>
              <div className="flex items-start gap-1.5 mb-1">
                <BigStat label="Lump Sum Needed Today" value={fmt(lumpResult.lump)} color="text-blue-400" />
                <Tooltip text="A one-time upfront investment made all at once — as opposed to saving a little each month." />
              </div>
              <div className="mt-6">
                <CoinStack lumpSum={lumpResult.lump} totalContrib={lumpResult.totalContrib} />
              </div>
            </CalcCard>
          </div>
        </div>
      )}

      {/* Subtab 3 — Monthly savings */}
      {subtab === 'pmt' && (
        <div className="grid grid-cols-1 md:grid-cols-[55%_43%] gap-6 items-start">
          <CalcCard>
            <div className="space-y-4">
              <InputRow label="Target Amount ($)"    prefix="$"  value={pGoal}  onChange={setPGoal}  step="10000" />
              <InputRow label="Starting Balance ($)" prefix="$"  value={pPV}    onChange={setPPV}    step="1000" />
              <SliderRow label="Annual Return" value={pRate} onChange={setPRate} min={1} max={15} step={0.5} display={`${pRate}%`} color="purple"
                tooltip="The average yearly growth rate of your investments. The S&P 500 has historically returned ~7%/year after inflation. Use 6-8% for a diversified stock portfolio, 2-4% for bonds." />
              <InputRow label="Years" suffix="yr" value={pYears} onChange={setPYears} step="1" min="1" max="50" />
            </div>
          </CalcCard>
          <div className="md:sticky md:top-6 md:self-start">
            <CalcCard>
              <BigStat label="Monthly Savings Needed" value={fmt(pmtResult.pmt)} color="text-purple-400" />
              <div className="mt-6 flex justify-center">
                <CircleRing pct={pmtResult.pct} amount={pmtResult.pmt} />
              </div>
            </CalcCard>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Calculator 5: 401k vs Roth vs Brokerage ───────────────────────────────────

/** End-of-period annuity future value: PMT × [(1+r)^n − 1] / r */
function annuityFV(pmt, r, n) {
  const p = pmt || 0, rate = r || 0, yrs = n || 0;
  if (rate < 1e-9) return p * yrs;
  return p * (Math.pow(1 + rate, yrs) - 1) / rate;
}

const fmtShort = (n) => {
  if (n == null || !isFinite(n)) return '$0';
  const v = Math.max(0, Math.round(n));
  if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(2) + 'M';
  if (v >= 1_000)     return '$' + (v / 1_000).toFixed(0) + 'K';
  return '$' + v.toLocaleString();
};

// ── 2026 Federal Tax Data (post-TCJA permanent extension) ─────────────────────

const FEDERAL_BRACKETS_2026 = {
  single: [
    [0, 0.10], [11925, 0.12], [48475, 0.22], [103350, 0.24],
    [197300, 0.32], [250525, 0.35], [626350, 0.37],
  ],
  marriedFilingJointly: [
    [0, 0.10], [23850, 0.12], [96950, 0.22], [206700, 0.24],
    [394600, 0.32], [501050, 0.35], [751600, 0.37],
  ],
  headOfHousehold: [
    [0, 0.10], [17000, 0.12], [64850, 0.22], [103350, 0.24],
    [197300, 0.32], [250500, 0.35], [626350, 0.37],
  ],
};

const STANDARD_DEDUCTIONS_2026 = {
  single: 15000,
  marriedFilingJointly: 30000,
  headOfHousehold: 22500,
};

const STATE_TAX_RATES = {
  AL:5.0, AK:0.0, AZ:4.5, AR:5.5, CA:9.3, CO:4.4, CT:6.99,
  DE:6.6, FL:0.0, GA:5.75, HI:11.0, ID:6.0, IL:4.95, IN:3.23,
  IA:8.53, KS:5.7, KY:5.0, LA:6.0, ME:7.15, MD:5.75, MA:5.0,
  MI:4.25, MN:9.85, MS:5.0, MO:5.3, MT:6.75, NE:6.84, NV:0.0,
  NH:0.0, NJ:10.75, NM:5.9, NY:6.85, NC:4.99, ND:2.5, OH:3.99,
  OK:5.0, OR:9.9, PA:3.07, RI:5.99, SC:7.0, SD:0.0, TN:0.0,
  TX:0.0, UT:4.85, VT:8.75, VA:5.75, WA:0.0, WV:6.5, WI:7.65,
  WY:0.0, DC:10.75,
};

const STATE_NAMES = {
  AL:'Alabama', AK:'Alaska', AZ:'Arizona', AR:'Arkansas', CA:'California',
  CO:'Colorado', CT:'Connecticut', DE:'Delaware', FL:'Florida', GA:'Georgia',
  HI:'Hawaii', ID:'Idaho', IL:'Illinois', IN:'Indiana', IA:'Iowa',
  KS:'Kansas', KY:'Kentucky', LA:'Louisiana', ME:'Maine', MD:'Maryland',
  MA:'Massachusetts', MI:'Michigan', MN:'Minnesota', MS:'Mississippi',
  MO:'Missouri', MT:'Montana', NE:'Nebraska', NV:'Nevada', NH:'New Hampshire',
  NJ:'New Jersey', NM:'New Mexico', NY:'New York', NC:'North Carolina',
  ND:'North Dakota', OH:'Ohio', OK:'Oklahoma', OR:'Oregon', PA:'Pennsylvania',
  RI:'Rhode Island', SC:'South Carolina', SD:'South Dakota', TN:'Tennessee',
  TX:'Texas', UT:'Utah', VT:'Vermont', VA:'Virginia', WA:'Washington',
  WV:'West Virginia', WI:'Wisconsin', WY:'Wyoming', DC:'Washington DC',
};

/** Returns federal marginal rate (%) from 2026 brackets after standard deduction */
function calculateFederalRate(income, filingStatus) {
  const std = STANDARD_DEDUCTIONS_2026[filingStatus] ?? 15000;
  const taxable = Math.max(0, income - std);
  const brackets = FEDERAL_BRACKETS_2026[filingStatus] ?? FEDERAL_BRACKETS_2026.single;
  for (let i = brackets.length - 1; i >= 0; i--) {
    if (taxable > brackets[i][0]) return brackets[i][1] * 100;
  }
  return brackets[0][1] * 100;
}

/** Returns { federal, state, combined } marginal rates (%) */
function calculateCombinedRate(income, filingStatus, stateCode) {
  const federal = calculateFederalRate(income, filingStatus);
  const state   = STATE_TAX_RATES[stateCode] ?? 0;
  return { federal, state, combined: federal + state };
}

/** Grows today's-dollar spending to nominal dollars at retirement (default 3% inflation) */
function calculateInflatedSpending(todaysSpending, yearsToRetirement, inflationRate = 0.03) {
  return todaysSpending * Math.pow(1 + inflationRate, yearsToRetirement);
}

/**
 * Returns { federal, state, combined, stdDeduct, inflatedSpending, taxableIncome }
 * Uses inflation-adjusted spending to look up the correct future tax bracket.
 */
function estimateFutureRates(retirementSpending, yearsToRetirement, filingStatus, stateCode) {
  const inflated  = calculateInflatedSpending(retirementSpending, yearsToRetirement);
  const rates     = calculateCombinedRate(inflated, filingStatus, stateCode);
  const stdDeduct = STANDARD_DEDUCTIONS_2026[filingStatus] ?? 15000;
  return {
    ...rates,
    stdDeduct,
    inflatedSpending: inflated,
    taxableIncome: Math.max(0, inflated - stdDeduct),
  };
}

function ComparisonBar({ label, value, maxValue, color, accent, tag }) {
  const pct = maxValue > 0 ? Math.max(4, (value / maxValue) * 100) : 4;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-300">{label}</span>
          {tag && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-emerald-900/40 border border-emerald-700/30 text-emerald-400">
              {tag}
            </span>
          )}
        </div>
        <span className={`text-sm font-bold tabular-nums ${accent}`}>{fmtShort(value)}</span>
      </div>
      <div className="h-3 bg-slate-700/60 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

export function RetirementComparison({ onBack }) {
  const { userProfile, budgetData } = useUserData();

  const annualIncome  = (userProfile?.annualIncome || 0) || (budgetData?.income || 0) * 12;
  const seedSalary    = Math.round(Math.max(30000, Math.min(300000, annualIncome || 70000)) / 1000) * 1000;
  const seedStatus    = userProfile?.taxFilingStatus === 'married_filing_jointly'
    ? 'marriedFilingJointly'
    : userProfile?.taxFilingStatus === 'head_of_household'
    ? 'headOfHousehold'
    : 'single';
  const seedState     = userProfile?.state || 'TX';

  // ── Core inputs ──────────────────────────────────────────────────────────────
  const [salary,       setSalary]       = useState(seedSalary);
  // salaryText is the raw string shown in the text field so users can type freely
  const [salaryText,   setSalaryText]   = useState(String(seedSalary));
  const [employeePct,  setEmployeePct]  = useState(6);
  const [employerPct,  setEmployerPct]  = useState(8);
  const [annualReturn, setAnnualReturn] = useState(7);
  const [years,        setYears]        = useState(40);

  // ── Tax inputs ───────────────────────────────────────────────────────────────
  const [filingStatus,           setFilingStatus]           = useState(seedStatus);
  const [stateCode,              setStateCode]              = useState(seedState);
  const [retirementSpending,     setRetirementSpending]     = useState(
    Math.round(Math.min(seedSalary * 0.7, 150000) / 5000) * 5000
  );
  const [overrideCurrentTax,  setOverrideCurrentTax]  = useState(false);
  const [customCurrentRate,   setCustomCurrentRate]   = useState(22);
  const [overrideFutureTax,   setOverrideFutureTax]   = useState(false);
  const [customFutureRate,    setCustomFutureRate]    = useState(22);

  // ── Auto-computed tax rates ──────────────────────────────────────────────────
  // Wrapped in try/catch so any unexpected input never crashes the render.
  const autoRates = useMemo(() => {
    try {
      const safeSal   = isFinite(salary) && salary > 0 ? salary : 70000;
      const safeRet   = isFinite(retirementSpending) && retirementSpending > 0 ? retirementSpending : 50000;
      const safeYears = isFinite(years) && years > 0 ? years : 40;
      const safeFs    = ['single', 'marriedFilingJointly', 'headOfHousehold'].includes(filingStatus)
                          ? filingStatus : 'single';
      const safeSc    = STATE_TAX_RATES[stateCode] !== undefined ? stateCode : 'TX';

      const stdDeduct    = STANDARD_DEDUCTIONS_2026[safeFs] ?? 15000;
      const current      = calculateCombinedRate(safeSal, safeFs, safeSc);
      const future       = estimateFutureRates(safeRet, safeYears, safeFs, safeSc);
      return {
        federal:      current.federal    ?? 22,
        state:        current.state      ?? 0,
        combined:     current.combined   ?? 22,
        stdDeduct,
        taxableIncome: Math.max(0, safeSal - stdDeduct),
        future: {
          federal:         future.federal          ?? 15,
          state:           future.state            ?? 0,
          combined:        future.combined         ?? 15,
          stdDeduct:       future.stdDeduct        ?? stdDeduct,
          inflatedSpending: future.inflatedSpending ?? safeRet,
          taxableIncome:   future.taxableIncome    ?? 0,
        },
      };
    } catch {
      // Safe fallback if anything goes wrong — component will still render
      return {
        federal: 22, state: 0, combined: 22,
        stdDeduct: 15000, taxableIncome: 55000,
        future: {
          federal: 15, state: 0, combined: 15,
          stdDeduct: 15000, inflatedSpending: 50000, taxableIncome: 35000,
        },
      };
    }
  }, [salary, filingStatus, stateCode, retirementSpending, years]);

  // toFix — safe wrapper so .toFixed() never throws on undefined/NaN
  const toFix = (n, dp = 1) => (isFinite(n) ? Number(n) : 0).toFixed(dp);

  const getCurrentTaxRate = () => overrideCurrentTax
    ? (isFinite(customCurrentRate) ? customCurrentRate : 22)
    : (autoRates?.combined ?? 22);
  const getFutureTaxRate  = () => overrideFutureTax
    ? (isFinite(customFutureRate) ? customFutureRate : 15)
    : (autoRates?.future?.combined ?? 15);

  // ── Core calculation ─────────────────────────────────────────────────────────
  const res = useMemo(() => {
    const curT = Math.max(0, Math.min(1, (getCurrentTaxRate() || 0) / 100));
    const futT = Math.max(0, Math.min(1, (getFutureTaxRate()  || 0) / 100));
    const safeSalary = isFinite(salary) && salary > 0 ? salary : 70000;
    const ePmt = safeSalary * ((isFinite(employeePct) ? employeePct : 6)  / 100);
    const mPmt = safeSalary * ((isFinite(employerPct) ? employerPct : 0)  / 100);
    const r    = isFinite(annualReturn) && annualReturn > 0 ? annualReturn / 100 : 0.07;
    const n    = isFinite(years) && years > 0 ? years : 40;

    const rothFV            = annuityFV(ePmt, r, n);
    const rothNet           = rothFV;

    const tradFVRaw         = annuityFV(ePmt, r, n);
    const tradFV            = tradFVRaw * (1 - futT);
    const taxSavingsPerYear = ePmt * curT;
    const taxSavingsFVRaw   = annuityFV(taxSavingsPerYear, r, n);
    const taxSavingsBasis   = taxSavingsPerYear * n;
    const taxSavingsGains   = Math.max(0, taxSavingsFVRaw - taxSavingsBasis);
    const taxSavingsNet     = taxSavingsFVRaw - taxSavingsGains * 0.20;
    const tradNet           = tradFV + taxSavingsNet;

    const afterTaxPmt    = ePmt * (1 - curT);
    const brokerageFV    = annuityFV(afterTaxPmt, r, n);
    const brokerageBasis = afterTaxPmt * n;
    const brokerageGains = Math.max(0, brokerageFV - brokerageBasis);
    const brokerageNet   = brokerageFV - brokerageGains * 0.20;

    const empFVRaw = annuityFV(mPmt, r, n);
    const empNet   = empFVRaw * (1 - futT);
    const maxNet   = Math.max(rothNet, tradNet, brokerageNet);

    return {
      ePmt, mPmt,
      rothNet, tradNet, brokerageNet, empNet, maxNet,
      tradFV, taxSavingsNet, brokerageFV, brokerageGains,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salary, employeePct, employerPct, annualReturn, years,
      overrideCurrentTax, customCurrentRate, overrideFutureTax, customFutureRate,
      autoRates]);

  const currentTaxDisplay = getCurrentTaxRate();
  const futureTaxDisplay  = getFutureTaxRate();
  const rothWins = res.rothNet >= res.tradNet;
  const tradDiff = Math.abs(res.tradNet - res.rothNet);
  const rothDiff = Math.abs(res.rothNet - res.tradNet);

  const FILING_LABELS = {
    single: 'Single',
    marriedFilingJointly: 'Married (Joint)',
    headOfHousehold: 'Head of Household',
  };


  return (
    <div>
      <BackLink onClick={onBack} />
      <h1 className="text-xl font-bold text-slate-100 mb-1">🏦 401k vs Roth vs Brokerage</h1>
      <CalcDesc>
        Compare three ways to invest the same dollars — traditional pre-tax 401k, Roth 401k, and a taxable
        brokerage. Tax rates are auto-calculated from 2026 federal brackets + your state. Override any rate for
        custom scenarios.
      </CalcDesc>

      <div className="grid grid-cols-1 md:grid-cols-[55%_43%] gap-6 items-start">

        {/* ── LEFT ── */}
        <div className="space-y-4">

          {/* Tip */}
          <div className="bg-blue-950/50 border border-blue-700/40 rounded-xl p-4">
            <div className="text-xs font-bold text-blue-300 uppercase tracking-wider mb-1.5">💡 How this works</div>
            <p className="text-sm text-slate-300 leading-relaxed">
              All three options invest the <strong className="text-slate-200">same gross salary dollars</strong> — the
              difference is <em>when</em> taxes are paid. Traditional saves tax now but pays later.
              Roth pays now but never again. Brokerage gets no tax break but offers full flexibility.
            </p>
          </div>

          {/* Contribution inputs */}
          <CalcCard>
            <h3 className="text-sm font-semibold text-slate-300 mb-4">Income &amp; Contributions</h3>
            <div className="space-y-5">
              {/* Salary */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-slate-500 uppercase tracking-wider">Annual Salary</label>
                  <span className="text-sm font-bold text-emerald-400">{fmtShort(salary)}</span>
                </div>
                <input type="range" min={30000} max={300000} step={1000} value={salary}
                  onChange={e => {
                    const v = parseFloat(e.target.value) || 30000;
                    setSalary(v);
                    setSalaryText(String(v));
                  }}
                  className="w-full accent-emerald-500 h-1.5 rounded-full mb-2"
                />
                <div className="flex items-center bg-slate-700/60 border border-slate-600 rounded-xl px-3 py-2 gap-2 focus-within:border-blue-500 transition-colors">
                  <span className="text-slate-400 font-medium flex-shrink-0">$</span>
                  <input
                    type="number" min={30000} max={300000} step={1000}
                    value={salaryText}
                    onChange={e => setSalaryText(e.target.value)}
                    onBlur={e => {
                      const parsed = parseFloat(e.target.value);
                      const clamped = isFinite(parsed)
                        ? Math.max(30000, Math.min(300000, parsed))
                        : salary;
                      setSalary(clamped);
                      setSalaryText(String(clamped));
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                    }}
                    className="flex-1 bg-transparent text-slate-100 font-semibold focus:outline-none tabular-nums min-w-0"
                  />
                </div>
              </div>

              <SliderRow label="Employee Contribution" value={employeePct} onChange={setEmployeePct}
                min={1} max={15} step={1} display={`${employeePct}% · ${fmtShort(res.ePmt)}/yr`} color="blue"
                tooltip="The % of salary you contribute. 2026 IRS limit: $23,500/yr for employee contributions."
              />
              <SliderRow label="Employer Match" value={employerPct} onChange={setEmployerPct}
                min={0} max={15} step={1} display={`${employerPct}% · ${fmtShort(res.mPmt)}/yr`} color="emerald"
                tooltip="Free money your employer adds. Common: 100% match up to 3%, or 50% up to 6%. Never leave it on the table."
              />
              <SliderRow label="Expected Annual Return" value={annualReturn} onChange={setAnnualReturn}
                min={4} max={12} step={0.5} display={`${annualReturn}%`} color="blue"
                tooltip="S&P 500 historical average: ~10% nominal, ~7% real (after inflation). Use 6–8% for a diversified stock/bond portfolio."
              />
              <SliderRow label="Years to Retirement" value={years} onChange={setYears}
                min={5} max={45} step={1} display={`${years} yrs`} color="cyan"
                tooltip="How many years until you retire. Longer = dramatically more compounding."
              />
            </div>
          </CalcCard>

          {/* ── Tax Section ── */}
          <CalcCard>
            <h3 className="text-sm font-semibold text-slate-300 mb-4">Tax Profile</h3>
            <div className="space-y-5">

              {/* Filing status chips */}
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider block mb-2">Filing Status</label>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(FILING_LABELS).map(([id, label]) => (
                    <button key={id} onClick={() => setFilingStatus(id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                        filingStatus === id
                          ? 'bg-amber-600/30 border-amber-500/50 text-amber-300'
                          : 'border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* State dropdown */}
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider block mb-1.5">State</label>
                <select value={stateCode} onChange={e => setStateCode(e.target.value)}
                  className="w-full bg-slate-700/60 border border-slate-600 rounded-xl px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                >
                  {Object.entries(STATE_NAMES)
                    .sort((a, b) => a[1].localeCompare(b[1]))
                    .map(([code, name]) => (
                      <option key={code} value={code}>
                        {name}{STATE_TAX_RATES[code] === 0 ? ' (no income tax)' : ` (${STATE_TAX_RATES[code]}% state)`}
                      </option>
                    ))}
                </select>
              </div>

              {/* Auto current tax breakdown */}
              <div className="bg-amber-950/30 border border-amber-700/30 rounded-xl p-3.5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-amber-300 uppercase tracking-wider">Current Tax Rate</span>
                  {!overrideCurrentTax
                    ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-700/40 border border-amber-600/50 text-amber-300 font-bold tracking-wide">AUTO</span>
                    : <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-600/60 border border-slate-500/50 text-slate-300 font-bold tracking-wide">OVERRIDE</span>
                  }
                </div>
                {!overrideCurrentTax ? (
                  <>
                    <div className="space-y-1.5 text-xs mb-3">
                      <div className="flex justify-between text-slate-400">
                        <span>Gross salary</span>
                        <span className="tabular-nums">{fmtShort(salary)}</span>
                      </div>
                      <div className="flex justify-between text-slate-400">
                        <span>Standard deduction ({FILING_LABELS[filingStatus]})</span>
                        <span className="tabular-nums text-rose-400">−{fmtShort(autoRates?.stdDeduct)}</span>
                      </div>
                      <div className="flex justify-between text-slate-300 font-medium pt-1 border-t border-slate-700/40">
                        <span>Taxable income</span>
                        <span className="tabular-nums">{fmtShort(autoRates?.taxableIncome)}</span>
                      </div>
                      <div className="flex justify-between text-slate-400 pt-1">
                        <span>Federal marginal (2026)</span>
                        <span className="text-amber-400 font-bold">{autoRates?.federal ?? 0}%</span>
                      </div>
                      <div className="flex justify-between text-slate-400">
                        <span>State ({stateCode})</span>
                        <span className="text-amber-400 font-bold">
                          {(autoRates?.state ?? 0) > 0 ? `${autoRates?.state}%` : 'None'}
                        </span>
                      </div>
                      <div className="flex justify-between text-slate-200 font-bold pt-1.5 border-t border-slate-700/40">
                        <span>Combined marginal rate</span>
                        <span className="text-amber-300 text-sm">{toFix(autoRates?.combined)}%</span>
                      </div>
                    </div>
                    <button onClick={() => { setOverrideCurrentTax(true); setCustomCurrentRate(Math.round(autoRates?.combined ?? 22)); }}
                      className="text-xs text-slate-500 hover:text-amber-400 transition-colors underline"
                    >
                      Override rate →
                    </button>
                  </>
                ) : (
                  <div className="space-y-3">
                    <SliderRow label="Override current rate" value={customCurrentRate} onChange={setCustomCurrentRate}
                      min={0} max={60} step={0.5} display={`${customCurrentRate}%`} color="amber" />
                    <button onClick={() => setOverrideCurrentTax(false)}
                      className="text-xs text-amber-400 hover:text-amber-300 transition-colors"
                    >
                      ← Use auto rate ({toFix(autoRates?.combined)}%)
                    </button>
                  </div>
                )}
              </div>

              {/* Expected retirement spending → drives future tax rate */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-slate-500 uppercase tracking-wider">Expected Annual Retirement Spending</label>
                  <span className="text-sm font-bold text-purple-400">{fmtShort(retirementSpending)}</span>
                </div>
                <input type="range" min={30000} max={200000} step={5000} value={retirementSpending}
                  onChange={e => setRetirementSpending(parseFloat(e.target.value))}
                  className="w-full accent-purple-500 h-1.5 rounded-full mb-1.5"
                />
                <p className="text-[11px] text-slate-500 leading-snug mb-3">
                  In <strong className="text-slate-400">today's purchasing power</strong> — we'll adjust for inflation automatically.
                  Think about what this amount buys you <em>now</em> for housing, healthcare, travel, food, etc.
                  Rule of thumb: <strong className="text-slate-400">70–80% of your salary</strong>
                  {' '}({fmtShort(Math.round(salary * 0.7 / 5000) * 5000)}–{fmtShort(Math.round(salary * 0.8 / 5000) * 5000)} for you).
                </p>

                {/* Auto future tax breakdown */}
                <div className="bg-purple-950/30 border border-purple-700/30 rounded-xl p-3.5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold text-purple-300 uppercase tracking-wider">Future Tax Rate at Retirement</span>
                    {!overrideFutureTax
                      ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-700/40 border border-purple-600/50 text-purple-300 font-bold tracking-wide">AUTO</span>
                      : <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-600/60 border border-slate-500/50 text-slate-300 font-bold tracking-wide">OVERRIDE</span>
                    }
                  </div>
                  {!overrideFutureTax ? (
                    <>
                      <div className="space-y-1.5 text-xs mb-3">
                        <div className="flex justify-between text-slate-400">
                          <span>Retirement spending (today's $)</span>
                          <span className="tabular-nums">{fmtShort(retirementSpending)}</span>
                        </div>
                        <div className="flex justify-between text-slate-300 font-medium">
                          <span>
                            Inflation-adjusted{' '}
                            <span className="text-slate-500 font-normal">
                              (3%/yr × {years}y)
                            </span>
                          </span>
                          <span className="tabular-nums text-orange-400">{fmtShort(autoRates?.future?.inflatedSpending)}</span>
                        </div>
                        <div className="flex justify-between text-slate-400 pt-1 border-t border-slate-700/40">
                          <span>Standard deduction</span>
                          <span className="tabular-nums text-rose-400">−{fmtShort(autoRates?.future?.stdDeduct)}</span>
                        </div>
                        <div className="flex justify-between text-slate-300 font-medium">
                          <span>Taxable retirement income</span>
                          <span className="tabular-nums">{fmtShort(autoRates?.future?.taxableIncome)}</span>
                        </div>
                        <div className="flex justify-between text-slate-400 pt-1 border-t border-slate-700/40">
                          <span>Federal marginal (2026)</span>
                          <span className="text-purple-400 font-bold">{autoRates?.future?.federal ?? 0}%</span>
                        </div>
                        <div className="flex justify-between text-slate-400">
                          <span>State ({stateCode})</span>
                          <span className="text-purple-400 font-bold">
                            {(autoRates?.future?.state ?? 0) > 0 ? `${autoRates?.future?.state}%` : 'None'}
                          </span>
                        </div>
                        <div className="flex justify-between text-slate-200 font-bold pt-1.5 border-t border-slate-700/40">
                          <span>Combined marginal rate</span>
                          <span className="text-purple-300 text-sm">{toFix(autoRates?.future?.combined)}%</span>
                        </div>
                      </div>
                      <button onClick={() => { setOverrideFutureTax(true); setCustomFutureRate(Math.round(getFutureTaxRate())); }}
                        className="text-xs text-slate-500 hover:text-purple-400 transition-colors underline"
                      >
                        Override rate →
                      </button>
                    </>
                  ) : (
                    <div className="space-y-3">
                      <SliderRow label="Override future rate" value={customFutureRate} onChange={setCustomFutureRate}
                        min={0} max={60} step={0.5} display={`${customFutureRate}%`} color="purple" />
                      <button onClick={() => setOverrideFutureTax(false)}
                        className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        ← Use auto rate ({toFix(autoRates?.future?.combined)}%)
                      </button>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </CalcCard>

          {/* Summary mini-stats */}
          <div className="grid grid-cols-3 gap-3">
            <MiniStat label="Your contribution/yr" value={fmtShort(res.ePmt)}             color="text-blue-400" />
            <MiniStat label="Employer match/yr"    value={fmtShort(res.mPmt)}             color="text-emerald-400" />
            <MiniStat label="Total into 401k/yr"   value={fmtShort(res.ePmt + res.mPmt)} color="text-slate-200" />
          </div>
        </div>

        {/* ── RIGHT (sticky) ── */}
        <div className="space-y-4 md:sticky md:top-6 md:self-start">

          {/* Comparison bars */}
          <CalcCard>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">
              After-Tax Value at Retirement ({years}y)
            </h3>
            <div className="space-y-4">
              <ComparisonBar label="Roth 401k" value={res.rothNet} maxValue={res.maxNet}
                color="#a78bfa" accent="text-violet-400" tag={rothWins ? 'BEST' : undefined} />
              <ComparisonBar label="Traditional + Reinvested Savings" value={res.tradNet} maxValue={res.maxNet}
                color="#60a5fa" accent="text-blue-400" tag={!rothWins ? 'BEST' : undefined} />
              <ComparisonBar label="Taxable Brokerage" value={res.brokerageNet} maxValue={res.maxNet}
                color="#475569" accent="text-slate-400" />
            </div>
            <div className="flex gap-3 mt-4 pt-3 border-t border-slate-700/40">
              <div className="flex-1 text-center">
                <div className="text-[11px] text-slate-500 mb-0.5">Current rate</div>
                <div className="text-xs font-bold text-amber-400">
                  {toFix(currentTaxDisplay)}%
                  {!overrideCurrentTax && <span className="ml-1 text-[9px] text-amber-600">AUTO</span>}
                </div>
              </div>
              <div className="w-px bg-slate-700" />
              <div className="flex-1 text-center">
                <div className="text-[11px] text-slate-500 mb-0.5">Future rate</div>
                <div className="text-xs font-bold text-purple-400">
                  {toFix(futureTaxDisplay)}%
                  {!overrideFutureTax && <span className="ml-1 text-[9px] text-purple-600">AUTO</span>}
                </div>
              </div>
            </div>
            <p className="text-[11px] text-slate-600 mt-3 leading-snug">
              Employee contribution only. Traditional includes reinvested annual tax savings (20% LTCG on gains).
            </p>
          </CalcCard>

          {/* Employer match */}
          <div className="bg-emerald-950/50 border border-emerald-700/40 rounded-xl p-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="text-xs font-bold text-emerald-300 uppercase tracking-wider">🎁 Employer Match</div>
              <span className="text-sm font-bold text-emerald-400 tabular-nums flex-shrink-0">{fmtShort(res.empNet)}</span>
            </div>
            <div className="h-2 bg-emerald-900/40 rounded-full overflow-hidden mb-3">
              <div className="h-full bg-emerald-500 rounded-full w-full" />
            </div>
            <p className="text-xs text-slate-400 leading-snug">
              {fmtShort(res.mPmt)}/yr grows to <strong className="text-emerald-300">{fmtShort(res.empNet)}</strong> after {years} years
              (taxed {toFix(futureTaxDisplay)}% on withdrawal). Always in addition to your own contributions.
            </p>
          </div>

          {/* Verdict */}
          {rothWins ? (
            <div className="bg-violet-950/50 border border-violet-700/40 rounded-xl p-4">
              <div className="text-xs font-bold text-violet-300 uppercase tracking-wider mb-2">Verdict</div>
              <p className="text-sm text-slate-200 leading-relaxed">
                <strong className="text-violet-300">Go Roth</strong> — you come out{' '}
                <strong className="text-violet-300">{fmtShort(rothDiff)}</strong> ahead.
                {currentTaxDisplay === futureTaxDisplay
                  ? ' At equal rates, Roth wins because you lock in today\'s rate on a smaller base.'
                  : futureTaxDisplay > currentTaxDisplay
                  ? ' Your future rate is higher — paying tax now saves money.'
                  : ' Even with a lower future rate, the math still favors Roth here.'}
              </p>
            </div>
          ) : (
            <div className="bg-blue-950/50 border border-blue-700/40 rounded-xl p-4">
              <div className="text-xs font-bold text-blue-300 uppercase tracking-wider mb-2">Verdict</div>
              <p className="text-sm text-slate-200 leading-relaxed">
                <strong className="text-blue-300">Traditional edges ahead</strong> by{' '}
                <strong className="text-blue-300">{fmtShort(tradDiff)}</strong> — but consider tax
                uncertainty. Future rates could rise. Most advisors recommend Roth at 22% or below regardless.
              </p>
            </div>
          )}

          {/* Brokerage note */}
          <div className="bg-slate-700/30 border border-slate-700/40 rounded-xl p-4">
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">On the Brokerage</div>
            <p className="text-xs text-slate-400 leading-snug">
              Use brokerage only <strong className="text-slate-300">after maxing your 401k ($23,500) and IRA ($7,000)</strong>.
              No tax break, no tax-deferred growth — but no contribution limits and no early withdrawal penalty.
            </p>
          </div>
        </div>
      </div>

      {/* ── Full-width: explanation + breakdown ── */}
      <CalcCard className="mt-6">
        <h3 className="text-sm font-semibold text-slate-200 mb-3">
          Why Roth at {toFix(currentTaxDisplay)}%? The Core Logic
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <div className="bg-slate-700/40 rounded-lg p-3">
            <div className="text-xs font-bold text-violet-400 uppercase tracking-wider mb-2">Roth 401k</div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Pay {toFix(currentTaxDisplay)}% tax <strong className="text-slate-300">today</strong>, then grow
              and withdraw completely tax-free. Best when your future rate equals or exceeds your current rate —
              very common for young or early-career earners.
            </p>
          </div>
          <div className="bg-slate-700/40 rounded-lg p-3">
            <div className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-2">Traditional 401k</div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Skip {toFix(currentTaxDisplay)}% now, pay {toFix(futureTaxDisplay)}% on every dollar
              withdrawn. Best when you expect significantly lower retirement income. Annual tax savings
              reinvested in brokerage help close the gap.
            </p>
          </div>
          <div className="bg-slate-700/40 rounded-lg p-3">
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Taxable Brokerage</div>
            <p className="text-xs text-slate-400 leading-relaxed">
              No upfront tax break. Pay {toFix(currentTaxDisplay)}% before investing, then 20% LTCG on
              gains at sale. Trails both 401k options but has{' '}
              <strong className="text-slate-300">no limits and full flexibility</strong>.
              Max tax-advantaged accounts first.
            </p>
          </div>
        </div>

        <div className="border-t border-slate-700/40 pt-4">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Full Breakdown</div>
          <div className="space-y-1">
            {[
              { label: 'Annual employee contribution',                            value: fmtShort(res.ePmt),                        color: 'text-slate-300' },
              { label: 'Roth 401k FV (fully tax-free)',                           value: fmtShort(res.rothNet),                     color: 'text-violet-400' },
              { label: `Traditional 401k FV (after ${toFix(futureTaxDisplay)}% tax)`, value: fmtShort(res.tradFV),              color: 'text-blue-400' },
              { label: 'Annual tax savings reinvested (Traditional)',             value: fmtShort(res.taxSavingsNet),               color: 'text-blue-300' },
              { label: 'Traditional total (401k + reinvested savings)',           value: fmtShort(res.tradNet),                     color: 'text-blue-400' },
              { label: 'Brokerage gross FV',                                      value: fmtShort(res.brokerageFV),                 color: 'text-slate-400' },
              { label: 'Minus: 20% LTCG on gains',                               value: `-${fmtShort(res.brokerageGains * 0.20)}`, color: 'text-rose-400' },
              { label: 'Brokerage net',                                           value: fmtShort(res.brokerageNet),                color: 'text-slate-400' },
              { label: 'Employer match net (traditional, shown separately)',      value: fmtShort(res.empNet),                      color: 'text-emerald-400' },
            ].map(row => (
              <div key={row.label} className="flex justify-between items-center py-1.5 border-b border-slate-700/40 last:border-0">
                <span className="text-slate-400 text-xs">{row.label}</span>
                <span className={`font-semibold tabular-nums text-xs ${row.color}`}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </CalcCard>
    </div>
  );
}
